import { useState, useCallback, useMemo } from 'react';
import { Property } from '../types';
import { ALLOWED_REFERRAL_PROPERTY_TYPES } from '../constants';
import { submitReferralToN8n, ReferralFormData } from '../services/referralService';
import { searchByCodesApi } from '../services/propertyApiClient';

const GOOGLE_SCRIPT_LOG_URL = "https://script.google.com/macros/s/AKfycbyvP3YHXlzjibp6O0SalARDE4rB8LCZ_-cewxbMxwgSVcJ4U14vnyf04hnzjvEfgygSLA/exec";

export const useReferralWizard = () => {
  const [step, setStep] = useState(1);
  const [isCiencuadrasProperty, setIsCiencuadrasProperty] = useState(true);
  const [isAbroad, setIsAbroad] = useState(false);
  const [searchCode, setSearchCode] = useState('');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<ReferralFormData>({
    nombre_broker: '', tipo_documento_broker: 'Cédula de ciudadanía', numero_documento_broker: '', countryCode_broker: '+57', telefono_broker: '', email_broker: '',
    nombre_cliente: '', tipo_documento_cliente: 'Cédula de ciudadanía', numero_documento_cliente: '', countryCode_cliente: '+57', telefono_cliente: '', email_cliente: '', ingresos_cliente: '',
  });

  const useInternalForm = useMemo(() => 
    isCiencuadrasProperty && !isAbroad && selectedBank === 'Davivienda', 
    [isCiencuadrasProperty, isAbroad, selectedBank]
  );

  const performSearch = useCallback(async (code: string) => {
    if (!code.trim()) return setSearchError("Por favor ingresa un código.");
    setIsSearching(true);
    setSearchError(null);
    setSelectedProperty(null);
    try {
      const res = await searchByCodesApi([code.trim()]);
      const found = res.items?.[0] || null;
      if (found) {
        if (!ALLOWED_REFERRAL_PROPERTY_TYPES.includes(found.tipoinmueble)) {
          setSearchError(`Inmueble "${found.tipoinmueble}" no apto. Solo: ${ALLOWED_REFERRAL_PROPERTY_TYPES.join(', ')}.`);
        } else {
          setSelectedProperty(found);
        }
      } else {
        setSearchError("No encontrado.");
      }
    } catch (e) {
      setSearchError("Error en la búsqueda.");
    } finally {
      setIsSearching(false);
    }
  }, []);

  const submitInternalForm = async () => {
    setSubmissionStatus('loading');
    setErrorMessage(null);
    try {
      await submitReferralToN8n(formData, selectedProperty, selectedBank);
      setSubmissionStatus('success');
    } catch (e: any) { 
      console.error("[ReferralWizard] Error:", e);
      setSubmissionStatus('error');
      setErrorMessage("Error de comunicación con el puente. Verifica tu conexión.");
    }
  };

  const handleLogBrokerData = async () => {
    const dataToSave = {
      nombre_broker: formData.nombre_broker, 
      tipo_documento_broker: formData.tipo_documento_broker,
      numero_documento_broker: formData.numero_documento_broker,
      telefono_broker: `${formData.countryCode_broker}${formData.telefono_broker}`, 
      email_broker: formData.email_broker,
      es_inmueble_ciencuadras: isCiencuadrasProperty, 
      ubicacion_cliente: isAbroad ? 'Exterior' : 'Nacional',
      banco_seleccionado: selectedBank, 
      codigo_inmueble: selectedProperty?.codigo || searchCode, 
      precio_inmueble: selectedProperty?.precio_venta || 0,
      fuente: "App Comercial",
      fecha_registro: new Date().toISOString()
    };

    try {
      await fetch('/api/referral/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSave),
      });
      fetch(GOOGLE_SCRIPT_LOG_URL, { method: 'POST', mode: 'no-cors', body: JSON.stringify(dataToSave) });
    } catch (e) { 
      console.error("[ReferralWizard] Error registrando auditoría:", e); 
    }
  };

  return {
    step, setStep, isCiencuadrasProperty, setIsCiencuadrasProperty, isAbroad, setIsAbroad,
    searchCode, setSearchCode, selectedProperty, isSearching, searchError, isLocked, setIsLocked,
    selectedBank, setSelectedBank, submissionStatus, formData, setFormData, useInternalForm,
    performSearch, handleLogBrokerData, submitInternalForm, setSelectedProperty, errorMessage
  };
};
